import { gql } from "@apollo/client";
import { MAX_RULE_GROUP_DEPTH } from "@ticketon/rewards-core";

/**
 * Reusable GraphQL fragments. Centralizing field selections here avoids
 * duplication across queries and keeps the handwritten result types (the
 * `TypedDocumentNode` generics in ./customer, ./promoter and ./admin, which
 * reuse the `@ticketon/rewards-core` models) aligned with what the server
 * returns.
 *
 * Fragment type conditions are the GraphQL type names the backend sets with
 * `[GraphQLName]` (Ticketon.Rewards README §4). Every entity selection includes
 * `id` so Apollo can normalize it.
 */

/** Fields selected on every `PagedResults<T>` connection (pageNumber is 1-based). */
export const PAGE_FIELDS = `
      count
      pageNumber
      pageSize
      hasNextPage
      hasPreviousPage
`;

/** `RewardMoney { amountMinor, currency }` selection. */
export const MONEY_FIELDS = `amountMinor
      currency`;

export const TIER_SUMMARY_FIELDS = gql`
  fragment TierSummaryFields on RewardTierSummary {
    id
    name
    rank
    icon
    color
    pointMultiplier
  }
`;

export const REWARD_BALANCE_FIELDS = gql`
  ${TIER_SUMMARY_FIELDS}
  fragment RewardBalanceFields on RewardBalance {
    memberId
    programId
    availablePoints
    pendingPoints
    lifetimeEarnedPoints
    lifetimeRedeemedPoints
    expiringPoints
    nextExpirationDate
    qualifyingPoints
    progressToNextTier
    pointsToNextTier
    currentTier {
      ...TierSummaryFields
    }
    nextTier {
      ...TierSummaryFields
    }
  }
`;

export const TRANSACTION_FIELDS = gql`
  fragment RewardTransactionFields on RewardTransaction {
    id
    memberId
    memberType
    programId
    type
    status
    points
    qualifyingPoints
    balanceAfter
    purchaseReference
    purchaseType
    eventReference
    promoterReference
    ruleReference
    description
    effectiveDate
    expirationDate
    reversalReference
    createdAt
  }
`;

export const CATALOG_ITEM_FIELDS = gql`
  fragment RewardCatalogItemFields on RewardCatalogItem {
    id
    programId
    name
    description
    rewardType
    pointsCost
    monetaryValue {
      amountMinor
      currency
    }
    currency
    inventory
    perMemberLimit
    startDate
    endDate
    imageUrl
    status
    fulfillmentInstructions
    terms
    eligibility {
      minTierRank
      allowedSegments
      allowedMemberTypes
    }
  }
`;

export const REDEMPTION_FIELDS = gql`
  fragment RedemptionFields on Redemption {
    id
    memberId
    quantity
    pointsSpent
    status
    redemptionCode
    fulfillmentStatus
    purchaseReference
    createdAt
    fulfilledAt
    expiresAt
    cancellationReason
    cancelledAt
    item {
      id
      name
      rewardType
      imageUrl
      description
      fulfillmentInstructions
      terms
    }
  }
`;

export const TIER_FIELDS = gql`
  fragment RewardTierFields on RewardTier {
    id
    programId
    name
    description
    rank
    icon
    minQualifyingPoints
    minQualifyingSpend {
      amountMinor
      currency
    }
    qualificationPeriod
    retentionPoints
    pointMultiplier
    color
    active
    benefits {
      id
      label
      description
      icon
    }
  }
`;

export const PROGRAM_FIELDS = gql`
  fragment RewardProgramFields on RewardProgram {
    id
    name
    code
    description
    memberType
    status
    pointsDisplayName
    pointLabelSingular
    pointLabelPlural
    startDate
    endDate
    defaultCurrency
    expirationPolicy
    pointsExpirationDays
    timeZone
    termsUrl
    termsContent
    audit {
      createdAt
      updatedAt
      createdBy
      updatedBy
    }
  }
`;

const RULE_CONDITION_FIELDS = `
    conditions {
      field
      operator
      value
      values
      range {
        min
        max
      }
    }`;

/**
 * Builds the recursive condition-group selection. GraphQL has no recursive
 * fragments, so the selection is unrolled to {@link MAX_RULE_GROUP_DEPTH}
 * levels — the deepest tree the rule builder lets an admin create — and every
 * level selects the same condition fields (including `range`).
 */
function buildConditionGroupSelection(depth: number): string {
  const nested = depth > 1 ? `
    groups {${buildConditionGroupSelection(depth - 1)}
    }` : "";
  return `
    operator${RULE_CONDITION_FIELDS}${nested}`;
}

export const RULE_CONDITION_GROUP_FIELDS = gql(`
  fragment RuleConditionGroupFields on RuleConditionGroup {${buildConditionGroupSelection(MAX_RULE_GROUP_DEPTH)}
  }
`);

export const EARNING_RULE_FIELDS = gql`
  ${RULE_CONDITION_GROUP_FIELDS}
  fragment EarningRuleFields on EarningRule {
    id
    programId
    name
    description
    status
    memberType
    priority
    stackable
    startDate
    endDate
    actions {
      id
      type
      amount
      rounding
      currency
    }
    limits {
      id
      type
      value
    }
    exclusions {
      excludeRefunded
      excludeWhenStackedWith
      excludedSalesChannels
    }
    conditions {
      ...RuleConditionGroupFields
    }
    audit {
      createdAt
      updatedAt
      createdBy
      updatedBy
    }
  }
`;

export const PROMOTION_FIELDS = gql`
  fragment RewardPromotionFields on RewardPromotion {
    id
    programId
    name
    description
    highlight
    startDate
    endDate
    imageUrl
    campaignCode
  }
`;

export const PROMOTER_INCENTIVE_FIELDS = gql`
  fragment PromoterIncentiveFields on PromoterIncentive {
    id
    programId
    name
    description
    startDate
    endDate
    targetDescription
    rewardDescription
    progress
  }
`;
